import { useEffect } from "react";
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  toast,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Button,
} from "@altimateai/lego";
import { Badge } from "@/uiCore";
import { Combobox, Stack } from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDatastore } from "@/helpers/apis";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { ConfigurationProps, DatabaseOption } from "./types";
import { fetchSnowflakeDatabases } from "@/helpers/apis";
import {
  FetchSnowflakeDatabasesRequest,
  SnowflakeDatastoreRequest,
  SnowflakeBasePayload,
  SnowflakeAuthKeyPair,
  SnowflakeConnectionType,
  DatastoreTypeEnum,
} from "../../../types";
import { IconChevronLeft } from "@tabler/icons-react";
import { LoadingButton, Spinner } from "@altimateai/lego";

const GET_SNOWFLAKE_DATABASES = "GET_SNOWFLAKE_DATABASES";

const formSchema = z.object({
  databases: z.array(z.string()).min(1, "Please select at least one database"),
});

type FormValues = z.infer<typeof formSchema>;

const SnowflakeConfiguration = ({ typeData, handleBack }: ConfigurationProps): JSX.Element => {
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      databases: [],
    },
  });

  const baseConfig: SnowflakeBasePayload = {
    account: typeData.connection?.account as string,
    user: typeData.connection?.username as string,
    password: typeData.connection?.password as string,
    role: typeData.connection?.role as string | undefined,
    warehouse: typeData.connection?.warehouse as string | undefined,
    name: typeData.name,
    type: typeData.type,
    description: typeData.description,
    key_pair: typeData.connection?.key_pair as SnowflakeAuthKeyPair,
    auth_type: typeData.connection?.auth_type as SnowflakeConnectionType,
  };

  const { data: databases, isFetching, isError, error } = useQuery({
    queryKey: [GET_SNOWFLAKE_DATABASES],
    queryFn: () =>
      fetchSnowflakeDatabases({
        ...baseConfig,
        database: "",
      } as FetchSnowflakeDatabasesRequest),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (databases) {
      const databaseValues = databases.map(db => db.database_name ?? "").filter(Boolean);
      form.setValue("databases", databaseValues, { shouldValidate: true });
    }
  }, [databases, form]);

  // Handle error (replaces onError callback removed in TanStack Query v5)
  useEffect(() => {
    if (isError && error) {
      const err = error as unknown as string;
      toast({
        variant: "destructive",
        title: "Unable to fetch databases",
        description: (
          <Stack direction={"column"} gap={"md"}>
            {Boolean(err) && <span>{err}</span>}
            <span>
              Please refer to{" "}
              <Link
                className={"hover:al-text-white !al-underline"}
                to={
                  "https://docs.altimate.ai/docs/snowflake-permissions-for-altimate-service-user"
                }
                target={"_blank"}
              >
                documentation
              </Link>
            </span>
          </Stack>
        ),
      });
    }
  }, [isError, error]);

  const { mutate: createDatastore, isPending: isLoading } = useMutation({
    mutationFn: (data: SnowflakeDatastoreRequest) => addDatastore(data),
    onSuccess: () => {
      navigate("/settings/datastores?refresh=true");
    },
    onError: (err: Error) => {
      toast({
        variant: "destructive",
        title: "Unable to add connection",
        description: err?.message ?? "",
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    createDatastore({
      ...baseConfig,
      databases: values.databases,
    });
  });

  const type = {
    name: "Snowflake",
    icon: getIconByDatastoreType({
      datastoreType: DatastoreTypeEnum.SNOWFLAKE,
      className: "al-w-10 al-h-10 al-mr-3",
    }),
  };
  const databaseOptions: DatabaseOption[] =
    databases?.map(db => ({
      value: db.database_name ?? "",
      label: db.database_name ?? "",
    })) || [];

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justify="between" className="al-items-center">
          <h3>Configuration</h3>
          <Badge variant="default" className={"al-m-2"} tooltip="">
            <Stack direction="row" className="al-items-center al-p-2">
              {type.icon}
              <span className="al-text-sm">{type.name}</span>
            </Stack>
          </Badge>
        </Stack>

        <div className="al-mt-6 al-space-y-4">
          <Form {...form}>
            <FormField
              control={form.control}
              name="databases"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Databases</FormLabel>
                  <FormControl>
                    {isFetching ? (
                      <Stack gap={"none"}>
                        <Spinner size={"sm"} />
                        <span className={"al-text-muted-foreground"}>Loading</span>
                      </Stack>
                    ) : (
                      <Combobox
                        placeholder="Select databases"
                        options={databaseOptions}
                        buttonProps={{ className: "!al-w-full" }}
                        value={field.value}
                        onChange={newValue => {
                          if (Array.isArray(newValue)) {
                            field.onChange(newValue);
                          }
                        }}
                        multiSelect
                        showClearButton
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Stack justify={"end"} direction="row" gap="xl">
              <Button variant={"outline"} asChild>
                <Link to="/settings/datastores">Cancel</Link>
              </Button>
              <Stack gap={"md"} className={"al-ps-4 al-border-l"}>
                <Button variant={"secondary"} onClick={handleBack}>
                  <IconChevronLeft size={16} />
                  Back
                </Button>
                <LoadingButton
                  variant="default"
                  type="submit"
                  loading={isLoading}
                  className="al-px-4"
                  disabled={!form.formState.isValid}
                  onClick={handleSubmit}
                >
                  Create
                </LoadingButton>
              </Stack>
            </Stack>
          </Form>
        </div>
      </CardContent>
    </Card>
  );
};

export default SnowflakeConfiguration;
